import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  Search,
  RefreshCw,
  ClipboardList,
  Clock,
  CheckCircle2,
  Play,
  FileText,
  Loader2,
} from 'lucide-react';
import {
  fetchRequests,
  fetchRequestReviewContext,
  executeRequest,
} from './api';

// ---------------------------------------------------------------------
// Wired to the real backend (fetchRequests / fetchRequestReviewContext /
// executeRequest) — only the list/filter/table layer was restyled to
// match the rest of the app. The date range is still what drives the
// actual backend fetch; search, request type, and status (now the 3
// cards below) are all filtered client-side from that one fetch, the
// same way PerformancePeriods.js does it. The Complete Request dialog
// itself is left as MUI, unchanged — a scope decision, same as the
// Classification/Confirm dialogs kept on other real pages.
// ---------------------------------------------------------------------

const REQUEST_TYPE_LABELS = {
  partial_withdrawal: 'Partial Withdrawal',
  full_withdrawal: 'Full Withdrawal',
  account_deactivation: 'Account Deactivation',
  savings_transfer: 'Savings Transfer',
  group_transfer: 'Group Transfer',
  account_offset: 'Account Offset',
};

const REQUEST_TYPE_COLORS = {
  partial_withdrawal: '#2196f3',
  full_withdrawal: '#f44336',
  account_deactivation: '#ff9800',
  savings_transfer: '#9c27b0',
  group_transfer: '#00bcd4',
  account_offset: '#4caf50',
};

const STATUS_CARDS = [
  { key: 'all', label: 'All', icon: ClipboardList },
  { key: 'approved', label: 'Approved', icon: Clock },
  { key: 'executed', label: 'Executed', icon: CheckCircle2 },
];

const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  approved: {
    icon: (isDark) => (isDark ? 'text-amber-400' : 'text-amber-600'),
    active: (isDark) => (isDark ? 'border-amber-500 bg-amber-900/20' : 'border-amber-500 bg-amber-50'),
  },
  executed: {
    icon: (isDark) => (isDark ? 'text-green-400' : 'text-green-600'),
    active: (isDark) => (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50'),
  },
};

const ExecuteRequest = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewContext, setReviewContext] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [statusFilter, setStatusFilter] = useState('all'); // driven by the cards
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Date range state - default to last 3 months
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setDate(today.getDate() - 90);

    return {
      from: threeMonthsAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    };
  });

  // Only the date range hits the backend again — status (cards) and
  // request type are filtered client-side from that one fetch below, so
  // switching a card or the type dropdown doesn't need a round trip.
  useEffect(() => {
    loadRequests();
  }, [dateRange]);

  const loadRequests = async () => {
    try {
      setLoading(true);

      const data = await fetchRequests({
        from_date: dateRange.from,
        to_date: dateRange.to,
      });
      const transformedRequests = data.results || data;

      // Only approved and executed requests belong here (exclude pending,
      // rejected, cancelled).
      const filteredByStatus = Array.isArray(transformedRequests)
        ? transformedRequests.filter(req => req.status === 'approved' || req.status === 'executed')
        : [];

      setRequests(filteredByStatus);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load requests',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Search + request type — everything except status, so all 3 cards can
  // be computed from one filtered set.
  const baseFiltered = useMemo(() => {
    let filtered = [...requests];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(req =>
        req.member_name?.toLowerCase().includes(query) ||
        req.group_name?.toLowerCase().includes(query) ||
        req.request_id?.toLowerCase().includes(query) ||
        req.member_id_display?.toLowerCase().includes(query)
      );
    }

    if (requestTypeFilter !== 'all') {
      filtered = filtered.filter(req => req.request_type === requestTypeFilter);
    }

    return filtered;
  }, [requests, searchQuery, requestTypeFilter]);

  const cardStats = useMemo(() => {
    return STATUS_CARDS.map(card => ({
      ...card,
      count: card.key === 'all' ? baseFiltered.length : baseFiltered.filter(r => r.status === card.key).length,
    }));
  }, [baseFiltered]);

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return baseFiltered;
    return baseFiltered.filter(r => r.status === statusFilter);
  }, [baseFiltered, statusFilter]);

  const getRequestTypeLabel = (type) => REQUEST_TYPE_LABELS[type] || type;
  const getRequestTypeColor = (type) => REQUEST_TYPE_COLORS[type] || '#757575';

  const getStatusBadgeClass = (status) => {
    if (status === 'approved') return isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800';
    if (status === 'executed') return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
    return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
  };

  const handleOpenDialog = async (request) => {
    try {
      setLoadingContext(true);
      setSelectedRequest(request);
      setShowDialog(true);

      const context = await fetchRequestReviewContext(request.id);
      setReviewContext(context);
    } catch (err) {
      console.error('Error fetching review context:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load request details',
        severity: 'error',
      });
    } finally {
      setLoadingContext(false);
    }
  };

  const handleExecuteRequest = async () => {
    if (!selectedRequest) return;

    setExecuting(true);

    try {
      await executeRequest(selectedRequest.id, { notes: '' });

      setSnackbar({
        open: true,
        message: 'Request executed successfully!',
        severity: 'success',
      });

      setShowDialog(false);
      setSelectedRequest(null);
      setReviewContext(null);
      loadRequests();
    } catch (err) {
      console.error('Error executing request:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to execute request',
        severity: 'error',
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleRefresh = () => {
    loadRequests();
  };

  return (
    <div className="pb-32">
      {/* Status cards - All / Approved / Executed */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {cardStats.map((card) => {
          const Icon = card.icon;
          const isActive = statusFilter === card.key;
          const accent = CARD_ACCENT[card.key];
          return (
            <button
              key={card.key}
              onClick={() => setStatusFilter(card.key)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : isDark
                    ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {card.count}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main panel: title, filters, table and footer count all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Execute Requests
            </h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Complete approved member requests for withdrawals and transfers.
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[240px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search by member, group, or request ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          <select
            value={requestTypeFilter}
            onChange={(e) => setRequestTypeFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Types</option>
            {Object.entries(REQUEST_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>From</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              style={{ colorScheme: isDark ? 'dark' : 'light' }}
              className={`px-3 py-2 border rounded-lg ${
                isDark ? 'bg-[#2b323c] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          <div className="flex items-center gap-2">
            <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>To</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              style={{ colorScheme: isDark ? 'dark' : 'light' }}
              className={`px-3 py-2 border rounded-lg ${
                isDark ? 'bg-[#2b323c] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading requests...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Request ID
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Member
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Request Type
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Requested Amount
                  </th>
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Approved By
                  </th>
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredRequests.map((request) => (
                  <tr key={request.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div className={`font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {request.request_id}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {new Date(request.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {request.member_name}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {request.group_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase"
                        style={{
                          backgroundColor: `${getRequestTypeColor(request.request_type)}20`,
                          color: getRequestTypeColor(request.request_type),
                        }}
                      >
                        {getRequestTypeLabel(request.request_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                        KSh {(request.requested_amount || 0).toLocaleString()}
                      </div>
                      {request.processing_charge > 0 && (
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Release: KSh {(request.release_amount || 0).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeClass(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                        {request.reviewed_by_name || 'N/A'}
                      </div>
                      {request.reviewed_at && (
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {new Date(request.reviewed_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {request.status === 'approved' ? (
                        <button
                          onClick={() => handleOpenDialog(request)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Play className="w-3.5 h-3.5" />
                          Complete
                        </button>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                          isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                        }`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Completed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredRequests.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <FileText className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No requests found</p>
            <p className="text-sm mt-1">Try adjusting your filters or search query</p>
          </div>
        )}

        {/* Footer count */}
        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredRequests.length} of {requests.length} requests
        </div>
      </div>

      {/* Execute Request Dialog — left as MUI, unchanged; this is a scope
         decision, same as the complex confirm/classification dialogs kept
         on the other real (backend-integrated) pages. */}
      <Dialog
        open={showDialog}
        onClose={() => !executing && setShowDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2b323c' : '#fff',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: isDark ? '#1e252e' : '#f5f5f5',
          borderBottom: `1px solid ${isDark ? '#3d4654' : '#e0e0e0'}`,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color={isDark ? '#fff' : 'inherit'}>
              Complete Request
            </Typography>
            <IconButton
              onClick={() => setShowDialog(false)}
              disabled={executing}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{
          borderColor: isDark ? '#3d4654' : '#e0e0e0',
        }}>
          {loadingContext ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedRequest && (
            <Box>
              {/* Request Overview */}
              <Box sx={{
                p: 2,
                mb: 2,
                borderRadius: 1,
                backgroundColor: isDark ? '#1e252e' : '#f5f5f5',
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Request ID
                    </Typography>
                    <Typography variant="body1" fontWeight="600" color={isDark ? '#fff' : 'inherit'}>
                      {selectedRequest.request_id}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ borderColor: isDark ? '#3d4654' : '#e0e0e0' }} />
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Member
                        </Typography>
                        <Typography variant="body2" fontWeight="500" color={isDark ? '#fff' : 'inherit'}>
                          {selectedRequest.member_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedRequest.member_id_display}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Group
                        </Typography>
                        <Typography variant="body2" fontWeight="500" color={isDark ? '#fff' : 'inherit'}>
                          {selectedRequest.group_name || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Financial Summary */}
              <Box sx={{
                p: 2,
                mb: 2,
                borderRadius: 1,
                backgroundColor: isDark ? '#1a2332' : '#e3f2fd',
              }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color={isDark ? '#fff' : 'inherit'}>
                  Financial Summary
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Requested Amount
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color={isDark ? '#fff' : 'inherit'}>
                      KSh {(selectedRequest.requested_amount || 0).toLocaleString()}
                    </Typography>
                  </Grid>
                  {selectedRequest.processing_charge > 0 && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Processing Charge
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="error">
                        - KSh {selectedRequest.processing_charge.toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1, borderColor: isDark ? '#3d4654' : 'rgba(0,0,0,0.12)' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Amount to Release
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      KSh {(selectedRequest.release_amount || selectedRequest.requested_amount || 0).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Request Details */}
              <Box sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: isDark ? '#1e252e' : '#fff',
                border: isDark ? '1px solid #3d4654' : '1px solid #e0e0e0',
              }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Request Type
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 4,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    mb: 2,
                    backgroundColor: getRequestTypeColor(selectedRequest.request_type) + '20',
                    color: getRequestTypeColor(selectedRequest.request_type),
                  }}
                >
                  {getRequestTypeLabel(selectedRequest.request_type)}
                </Box>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Reason for Request
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }} color={isDark ? '#fff' : 'inherit'}>
                  {selectedRequest.reason || 'No reason provided'}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">
                  Approved By
                </Typography>
                <Typography variant="body2" color={isDark ? '#fff' : 'inherit'}>
                  {selectedRequest.reviewed_by_name || 'N/A'} {selectedRequest.reviewed_at && `on ${new Date(selectedRequest.reviewed_at).toLocaleString()}`}
                </Typography>
              </Box>

              {/* Review Context */}
              {reviewContext && (
                <Box sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: 1,
                  backgroundColor: isDark ? '#1e252e' : '#f5f5f5',
                }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Additional Context
                  </Typography>
                  {reviewContext.current_balance && (
                    <Typography variant="body2" color={isDark ? '#fff' : 'inherit'}>
                      Current Balance: KSh {reviewContext.current_balance.toLocaleString()}
                    </Typography>
                  )}
                  {reviewContext.loan_balance && (
                    <Typography variant="body2" color={isDark ? '#fff' : 'inherit'}>
                      Loan Balance: KSh {reviewContext.loan_balance.toLocaleString()}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: 2,
          backgroundColor: isDark ? '#1e252e' : '#f5f5f5',
          borderTop: `1px solid ${isDark ? '#3d4654' : '#e0e0e0'}`,
        }}>
          <Button
            onClick={() => setShowDialog(false)}
            disabled={executing}
            sx={{ color: isDark ? '#fff' : 'inherit' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExecuteRequest}
            disabled={executing || loadingContext}
            startIcon={executing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: '#4caf50',
              '&:hover': {
                backgroundColor: '#45a049',
              },
            }}
          >
            {executing ? 'Processing...' : 'Complete Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ExecuteRequest;